import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

interface Followup {
  id: string;
  customerId: string;
  dueAt: string;
  note: string;
  assignedTo: string;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
  completedBy: string | null;
}

export function FollowupsPanel({ customerId }: { customerId: string }) {
  const [items, setItems] = useState<Followup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [dueAt, setDueAt] = useState(defaultDue());
  const [posting, setPosting] = useState(false);

  // The SIGNED-IN user's email, from /api/me. This was
  // `VITE_MOCK_USER_EMAIL || 'dev.user@cbtravel.example'` — a dev placeholder set
  // in no build, so every request here asked the server for a user that does not
  // exist and rendered the empty result as though it were the answer.
  const { me: identity } = useMe();
  const me = (identity?.email ?? '').toLowerCase();

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ followups: Followup[] }>(
        `/api/followups?customer_id=${encodeURIComponent(customerId)}&include_completed=1`,
      );
      setItems(data.followups);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const submit = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      await apiFetch('/api/followups', {
        method: 'POST',
        body: JSON.stringify({
          customer_id: customerId,
          due_at: new Date(dueAt).toISOString(),
          note: note.trim(),
        }),
      });
      setNote('');
      setDueAt(defaultDue());
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  };

  const toggle = async (f: Followup) => {
    const prev = items;
    const done = !f.completedAt;
    setItems(
      items.map((x) =>
        x.id === f.id
          ? {
              ...x,
              completedAt: done ? new Date().toISOString() : null,
              completedBy: done ? me : null,
            }
          : x,
      ),
    );
    try {
      await apiFetch(`/api/followups/${f.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ completed: done }),
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setItems(prev);
    }
  };

  const open = items.filter((x) => !x.completedAt);
  const closed = items.filter((x) => x.completedAt);

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-label">Follow-ups</span>
        <span className="section-meta">
          {open.length} open · {closed.length} done
        </span>
      </div>
      <div className="section-body">
        {error && <div className="banner banner-error">{error}</div>}

        <div
          className="form-grid"
          style={{ gap: 8, alignItems: 'flex-end', marginBottom: 14 }}
        >
          <div>
            <label className="field-label">Reminder</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Circle back about Q3 plan…"
              className="input"
              disabled={posting}
            />
          </div>
          <div>
            <label className="field-label">Due</label>
            <input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="input"
              disabled={posting}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={posting || !note.trim()}
          >
            Schedule
          </button>
        </div>

        {loading && items.length === 0 ? (
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              fontSize: 'var(--fs-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            No follow-ups scheduled yet.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {[...open, ...closed].map((f) => {
              const due = new Date(f.dueAt);
              const overdue = !f.completedAt && due < new Date();
              return (
                <li
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: '1px solid var(--border-light)',
                    opacity: f.completedAt ? 0.6 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(f.completedAt)}
                    onChange={() => toggle(f)}
                    style={{
                      width: 16,
                      height: 16,
                      marginTop: 3,
                      accentColor: 'var(--accent)',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'var(--fs-md)',
                        color: 'var(--text-primary)',
                        textDecoration: f.completedAt ? 'line-through' : 'none',
                      }}
                    >
                      {f.note}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: overdue
                          ? 'var(--red-strong)'
                          : 'var(--text-muted)',
                        marginTop: 3,
                        fontWeight: overdue ? 600 : 500,
                      }}
                    >
                      Due{' '}
                      {due.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {overdue && ' · overdue'} · {f.assignedTo}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function defaultDue(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}
